import { hijackEffects } from 'stop-runaway-react-effects';
import { HelmetProvider } from 'react-helmet-async';
import { configure } from '@mfw/firebase';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import React from 'react';

// Global, not left to `FormRichTextField`: every read-only Quill view
// (`ExpandableQuill`, the course/episode/module viewers, notifications…) needs
// it too, and the form now ships only on the screens that edit rich text. Quill
// 2 draws list bullets from this sheet — without it a bullet list renders as a
// numbered one. Before `main.scss`, the order it had in the app shell before.
import 'react-quill-new/dist/quill.snow.css';
import './assets/scss/main.scss';
import { FullPageSpinner } from './components/Spinner';
import { buildSentryOptions, shouldInitSentry } from './config/sentryRuntime';
import { installKeylessRejectionReporter } from './config/keylessRejection';
import { getQueryClientLoaderInstance } from './context/ApiClientProvider';
import { trackAnsweredMutationErrors } from './context/answeredApiError';
import { dropAnsweredRejections } from './config/answeredRejection';
import { applyThemeToRoot, type ThemeColors } from './context/AppThemeProvider';
import { readBrandingCache } from './theme/brandingCache';
import config from './config';
import App from './App.tsx';

configure({
  firebase: {
    config: {
      apiKey: config.firebase.apiKey,
      appId: config.firebase.appId,
      authDomain: config.firebase.authDomain,
      measurementId: config.firebase.measurementId,
      messagingSenderId: config.firebase.messagingSenderId,
      projectId: config.firebase.projectId,
      storageBucket: config.firebase.storageBucket,
    },
  },
});

if (config.environment.isLocal) {
  hijackEffects({ callCount: 600, timeLimit: 1000 });
}

// WHETHER to report and WHAT to say are both decided in `config/sentryRuntime`,
// where they are pinned by tests — this file only supplies the two things that
// cannot be pure: where the page is actually running, and the SDK integrations.
if (
  shouldInitSentry({
    dsn: config.sentry.dsn,
    hostname: window.location.hostname,
    isLocalBuild: config.environment.isLocal,
  })
) {
  // V2-34/V2-70: a `mutateAsync` nobody awaited rejects with an error the user
  // was already shown as a toast. Recognized here once, for every call site.
  const queryClient = getQueryClientLoaderInstance();
  const answeredErrors = queryClient
    ? trackAnsweredMutationErrors(queryClient)
    : null;
  const wasAnswered = (error: unknown) =>
    answeredErrors?.wasAnswered(error) ?? false;

  Sentry.init({
    ...buildSentryOptions({
      dsn: config.sentry.dsn,
      env: config.sentry.env,
      release: config.sentry.release,
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: config.sentry.tracePropagationTargets ?? [
        'localhost',
      ],
    }),
    beforeSend: dropAnsweredRejections(wasAnswered),
    integrations: [Sentry.browserTracingIntegration()],
  });
  // Replay joins once its chunk arrives rather than riding in the first-paint
  // bundle. The cost: the error-replay buffer starts that much later, so an
  // error in the first moments after boot has no replay attached. A chunk that
  // fails to load only means no replay — never an error of its own.
  import('./config/sentryReplay')
    .then(({ replayIntegration }) => Sentry.addIntegration(replayIntegration()))
    .catch(() => undefined);
  // V2-34: gives a keyless rejected value (a CloseEvent, an ErrorEvent, a
  // prototype-backed instance) the shape the SDK's own report lacks.
  installKeylessRejectionReporter(
    (message, extra) => {
      Sentry.captureMessage(message, { extra: { ...extra }, level: 'warning' });
    },
    window,
    wasAnswered,
  );
}

// Before anything renders: the branding this device showed last, so the boot
// spinner and the first frame of the app already wear it instead of the
// catalog defaults (see `theme/brandingCache`). No cache: the spinner stays
// neutral until `AppThemeProvider` knows the live branding.
const cachedBranding = readBrandingCache();
if (cachedBranding) {
  applyThemeToRoot(
    document.documentElement,
    cachedBranding.theme as ThemeColors,
    cachedBranding.glow,
    cachedBranding.calendarColours,
  );
}
document.documentElement.dataset.mfwBranding = cachedBranding
  ? 'ready'
  : 'pending';

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <HelmetProvider>
      {/*
        One of the app's two boot gates, and the FUTURE-PROOF one rather than
        the one that fires today: this catches a suspending import, while what
        a cold load actually waits on is the router resolving its first route
        module (`App.tsx`, `fallbackElement`). It still has to be here — this
        boundary sits above every provider, so nothing below it could supply a
        fallback in its place, and with none of its own React renders `null`.
      */}
      <React.Suspense fallback={<FullPageSpinner />}>
        <App />
      </React.Suspense>
    </HelmetProvider>
  </React.StrictMode>,
);
